// Company stores (S4 shop, 2026-10-12) — the store admin's "Cancel order"
// (POST /portal/store/admin/orders/:id/cancel), for an order that's waiting
// for approval, approved, or waiting for payment. Asks first. Until the API
// has the route it answers 404, and this says so in plain words.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { apiMessage } from "@/lib/portal-store";
import { SECONDARY } from "@/components/StoreShopUi";
import { cancelStoreOrder, canCancel, STORE_ORDERS_KEY, STORE_PENDING_KEY, storeOrderKey, type StoreOrder } from "@/lib/store-shop";

export function StoreCancelOrder({ order, onCancelled }: { order: Pick<StoreOrder, "id" | "status" | "reference">; onCancelled?: () => void }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const cancel = useMutation({
    mutationFn: () => cancelStoreOrder(order.id),
    onSuccess: () => {
      setAsking(false);
      void qc.invalidateQueries({ queryKey: STORE_ORDERS_KEY });
      void qc.invalidateQueries({ queryKey: STORE_PENDING_KEY });
      void qc.invalidateQueries({ queryKey: storeOrderKey(order.id) });
      void qc.invalidateQueries({ queryKey: ["portal-store-admin-orders"] });
      onCancelled?.();
    },
  });
  if (!canCancel(order.status)) return null;
  const err = cancel.error;
  const message =
    err instanceof ApiError && err.status === 404
      ? "Cancelling online isn't switched on yet. Please ask your rep at other to cancel it."
      : err
        ? apiMessage(err)
        : null;

  if (!asking) {
    return (
      <div className="flex flex-col gap-1">
        <button type="button" className={SECONDARY} onClick={() => setAsking(true)}>
          Cancel order
        </button>
        {message && <p className="text-xs text-rust-500">{message}</p>}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-rule bg-canvas p-3 text-sm">
      <p className="text-ink">Cancel order {order.reference}? Nothing is made or charged for a cancelled order.</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
          className="inline-flex min-h-[40px] items-center rounded-md bg-rust-500 px-3.5 text-sm font-semibold text-white hover:bg-rust-600 disabled:opacity-50"
        >
          {cancel.isPending ? "Cancelling…" : "Yes, cancel it"}
        </button>
        <button type="button" className={SECONDARY} disabled={cancel.isPending} onClick={() => setAsking(false)}>
          Keep it
        </button>
      </div>
      {message && <p role="alert" className="text-xs text-rust-500">{message}</p>}
    </div>
  );
}
